/**
 * InsightVariablesTab — Generation form + history list for insight-derived variables.
 *
 * Placed inside InsightsDetailDialog as the 6th tab.
 * Contains:
 *   - Category selector (checkboxes with counts)
 *   - Read-only task scope inherited from the insights header selector
 *   - AvailableModelSelector
 *   - Generate button with progress tracking
 *   - History list of past generations
 *   - Review panel for selected generation
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Stack, Box, Typography, Button, Chip, Checkbox,
  FormControlLabel, FormGroup,
  CircularProgress, Alert, Divider,
  List, ListItemButton, ListItemText, ListItemSecondaryAction,
  IconButton, LinearProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { DEFAULT_MODEL } from '@modules/ui-components';
import { AvailableModelSelector } from '@modules/ai-settings';
import { AgentApiClient } from '@modules/content-agent';
import { useTask } from '@modules/background-tasks';
import {
  InsightVariableGenerationService,
  type InsightVariableGenerationData,
  type GeneratedVariableEntityData,
} from '@modules/firebase-core/services/InsightVariableGenerationService';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import { InsightVariableReviewPanel } from './InsightVariableReviewPanel';
import { toDate } from '../utils/toDate';

// ── Types ────────────────────────────────────────────────────────────

const JTBD_CATEGORIES = [
  'coreJobs', 'contexts', 'fears', 'bigJobs',
  'subtasks', 'competitors', 'habits',
] as const;

const CATEGORY_LABELS: Record<string, string> = {
  coreJobs: 'Core Jobs',
  contexts: 'Contexts',
  fears: 'Fears',
  bigJobs: 'Big Jobs',
  subtasks: 'Subtasks',
  competitors: 'Competitors',
  habits: 'Habits',
};

interface InsightVariablesTabProps {
  analysisId: string;
  analysisName: string;
  projectId: string;
  aggregatedInsights: Record<string, any[]>;
  categoryQuestionSets?: Record<string, any>;
  identificationMatrix?: Record<string, any>;
  discriminativeQuestions?: any[];
  affinityRules?: any[];
  selectedActorRefs?: any[];
  taskScope?: 'global' | 'task';
  taskId?: string;
  taskName?: string;
  hasTaskPackage?: boolean;
  taskTargets?: Array<{ taskId: string; taskName?: string; hasPackage?: boolean }>;
  allTaskCategoryCounts?: Record<string, number>;
  onTaskStarted?: (taskId: string, description: string) => void;
  /** R6 deep-link: preselect this generation and open its review panel. */
  initialGenerationId?: string;
}

// ── Status badge ─────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'> = {
  running: 'info',
  completed: 'warning',
  reviewed: 'primary',
  applied: 'success',
  error: 'error',
};

// ── Component ────────────────────────────────────────────────────────

export const InsightVariablesTab: React.FC<InsightVariablesTabProps> = ({
  analysisId,
  analysisName,
  projectId,
  aggregatedInsights,
  categoryQuestionSets,
  identificationMatrix,
  discriminativeQuestions,
  affinityRules,
  selectedActorRefs,
  taskScope = 'global',
  taskId,
  taskName,
  hasTaskPackage = true,
  taskTargets = [],
  allTaskCategoryCounts,
  onTaskStarted,
  initialGenerationId,
}) => {
  // Get userId from Firebase Auth (universal — no prop drilling needed)
  const userId = CoreFirebaseService.getInstance().auth.currentUser?.uid || '';
  // ── Generation form state ──────────────────────────────────────────
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [model, setModel] = useState<string>(DEFAULT_MODEL);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── History state ──────────────────────────────────────────────────
  const [generations, setGenerations] = useState<InsightVariableGenerationData[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [selectedGenerationId, setSelectedGenerationId] = useState<string | null>(null);

  // ── Active task tracking (via background-tasks store — auto-detected) ──
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);
  const activeTask = useTask(activeTaskId ?? '');

  const service = useMemo(() => new InsightVariableGenerationService(projectId), [projectId]);
  const effectiveTaskScope: 'global' | 'task' = taskScope === 'task' ? 'task' : 'global';
  const taskTargetsWithPackages = useMemo(
    () => taskTargets.filter(target => target.taskId && target.hasPackage !== false),
    [taskTargets],
  );
  const taskNameById = useMemo(() => {
    const entries = taskTargets
      .filter(target => target.taskId)
      .map(target => [target.taskId, target.taskName || target.taskId] as const);
    if (taskId) entries.push([taskId, taskName || taskId] as const);
    return new Map(entries);
  }, [taskTargets, taskId, taskName]);
  const canGenerateSelectedTask = effectiveTaskScope === 'task' && Boolean(taskId) && hasTaskPackage;
  const canGenerateAllTasks = taskTargetsWithPackages.length > 0;
  const canUseSelectedTaskScope = canGenerateSelectedTask;
  const expectedVariableTaskIds = useMemo(
    () => (effectiveTaskScope === 'task' && taskId ? [taskId] : []),
    [effectiveTaskScope, taskId],
  );

  const scopedGenerations = useMemo(() => generations.filter((gen) => {
    if (gen.analysisId !== analysisId) return false;
    if (effectiveTaskScope === 'task') return gen.taskId === taskId;
    return gen.taskScope === 'task';
  }), [generations, analysisId, effectiveTaskScope, taskId]);

  // ── Load history ──────────────────────────────────────────────────
  useEffect(() => {
    const unsubscribe = service.subscribe(
      (items) => {
        setGenerations(items);
        setIsLoadingHistory(false);
      },
      (err) => {
        console.error('InsightVariablesTab: subscription error', err);
        setIsLoadingHistory(false);
      },
    );
    return unsubscribe;
  }, [service]);

  useEffect(() => {
    if (selectedGenerationId && !scopedGenerations.some(gen => gen.id === selectedGenerationId)) {
      setSelectedGenerationId(null);
    }
  }, [scopedGenerations, selectedGenerationId]);

  // R6 deep-link: preselect the requested generation once it appears.
  const appliedInitialGenerationRef = useRef<string | null>(null);
  useEffect(() => {
    if (!initialGenerationId || appliedInitialGenerationRef.current === initialGenerationId) return;
    if (generations.some(gen => gen.id === initialGenerationId)) {
      appliedInitialGenerationRef.current = initialGenerationId;
      setSelectedGenerationId(initialGenerationId);
    }
  }, [initialGenerationId, generations]);

  // R6: per-generation accepted counts for the history-row chips.
  const [acceptedCounts, setAcceptedCounts] = useState<Record<string, { accepted: number; total: number }>>({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(scopedGenerations.map(async (gen) => {
        try {
          const vars = await service.listVariables(gen.id);
          return [gen.id, {
            accepted: vars.filter(v => v.accepted === true).length,
            total: vars.length,
          }] as const;
        } catch {
          return [gen.id, { accepted: 0, total: 0 }] as const;
        }
      }));
      if (!cancelled) setAcceptedCounts(Object.fromEntries(entries));
    })();
    return () => { cancelled = true; };
  }, [service, scopedGenerations, generations]);

  // R6: opening a generation must be visible — scroll the review panel into view.
  const reviewPanelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (selectedGenerationId) {
      requestAnimationFrame(() => {
        reviewPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [selectedGenerationId]);

  // ── Category counts ───────────────────────────────────────────────
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const cat of JTBD_CATEGORIES) {
      counts[cat] = effectiveTaskScope === 'global'
        ? allTaskCategoryCounts?.[cat] ?? 0
        : aggregatedInsights[cat]?.length ?? 0;
    }
    return counts;
  }, [aggregatedInsights, allTaskCategoryCounts, effectiveTaskScope]);

  // ── Toggle category ───────────────────────────────────────────────
  const toggleCategory = useCallback((category: string) => {
    setSelectedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category],
    );
  }, []);

  // ── Generate ──────────────────────────────────────────────────────
  const handleGenerate = useCallback(async (mode: 'selected-task' | 'all-tasks') => {
    const isAllTasks = mode === 'all-tasks';
    if (selectedCategories.length === 0 || isSubmitting) return;
    if (!isAllTasks && !canGenerateSelectedTask) return;
    if (isAllTasks && !canGenerateAllTasks) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const client = new AgentApiClient();
      const response = await client.generateInsightVariables({
        projectId,
        userId,
        analysisId,
        categories: selectedCategories,
        taskScope: isAllTasks ? 'all_tasks' : 'task',
        taskId: isAllTasks ? undefined : taskId,
        taskIds: isAllTasks ? taskTargetsWithPackages.map(target => target.taskId) : undefined,
        model,
      });

      if (!response.success) {
        setError(response.error || 'Failed to start generation');
        return;
      }

      const startedTasks = Array.isArray(response.tasks) ? response.tasks : [];
      if (startedTasks.length > 0) {
        setActiveTaskId(startedTasks[0].taskId);
        for (const item of startedTasks) {
          onTaskStarted?.(
            item.taskId,
            `Variables: ${item.taskName || taskNameById.get(item.sourceTaskId) || item.sourceTaskId}`,
          );
        }
      } else if (response.taskId) {
        setActiveTaskId(response.taskId);
        onTaskStarted?.(response.taskId, `Variables: ${selectedCategories.map(c => CATEGORY_LABELS[c]).join(', ')}`);
      }

      const firstGenerationId = startedTasks[0]?.generationId || response.generationId;
      if (firstGenerationId) {
        setSelectedGenerationId(firstGenerationId);
      }

      setSelectedCategories([]);
    } catch (err: any) {
      setError(err.message || 'Failed to start generation');
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedCategories, isSubmitting, canGenerateSelectedTask, canGenerateAllTasks, projectId, userId, analysisId, taskId, taskTargetsWithPackages, model, onTaskStarted, taskNameById]);

  // ── Delete generation ─────────────────────────────────────────────
  const handleDelete = useCallback(async (generationId: string) => {
    try {
      await service.delete(generationId);
      if (selectedGenerationId === generationId) {
        setSelectedGenerationId(null);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete');
    }
  }, [service, selectedGenerationId]);

  // ── File input ref for import ──────────────────────────────────────
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // ── Export insights data for variable-generator skill eval ────────
  const handleExportForEval = useCallback(() => {
    if (selectedCategories.length === 0) {
      setError('Select at least one category to export');
      return;
    }

    // Build VariableGenerationInput from analysis data (same as agent Phase 1)
    const selectedInsights: Record<string, any[]> = {};
    const selectedCategoryQuestionSets: Record<string, any> = {};
    for (const cat of selectedCategories) {
      selectedInsights[cat] = aggregatedInsights[cat] ?? [];
      if (categoryQuestionSets?.[cat]) {
        selectedCategoryQuestionSets[cat] = categoryQuestionSets[cat];
      }
    }

    const exportData = {
      __segmently_variable_eval__: true,
      version: '1.0.0',
      exportedAt: new Date().toISOString(),
      data: {
        analysisId,
        analysisName,
        categories: selectedCategories,
        taskScope: effectiveTaskScope,
        taskId: effectiveTaskScope === 'task' ? taskId : undefined,
        selectedInsights,
        categoryQuestionSets: selectedCategoryQuestionSets,
        identificationMatrix: identificationMatrix ?? {},
        discriminativeQuestions: discriminativeQuestions ?? [],
        affinityRules: affinityRules ?? [],
        selectedActorRefs: selectedActorRefs ?? [],
        tasks: effectiveTaskScope === 'task' && taskId
          ? [{ id: taskId, name: taskName || taskId, description: '' }]
          : [],
        existingVariables: [],
      },
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `variable-input-eval-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [selectedCategories, aggregatedInsights, categoryQuestionSets, analysisId, analysisName, effectiveTaskScope, taskId, taskName, identificationMatrix, discriminativeQuestions, affinityRules, selectedActorRefs]);

  // ── Import variable result (from skill eval) ─────────────────────
  const handleImport = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset input so same file can be re-imported
    event.target.value = '';

    try {
      const text = await file.text();
      const parsed = JSON.parse(text);

      // Accept both envelope and raw InsightVariableResult
      const variables = parsed.__segmently_variable_import__
        ? parsed.variables
        : parsed.variables;
      const explanation = parsed.__segmently_variable_import__
        ? parsed.explanation
        : parsed.explanation;
      const source = parsed.source ?? (parsed.__segmently_variable_import__ ? 'skill-eval' : 'imported');

      if (!variables?.length || !explanation) {
        setError('Invalid file: must contain "variables" array and "explanation" object');
        return;
      }

      const availableInsightIds = new Set(
        Object.values(aggregatedInsights).flatMap((items) => (Array.isArray(items) ? items : []).map((item: any) => item.id)),
      );
      const sameTaskIds = (actual: unknown) => (
        Array.isArray(actual)
        && actual.length === expectedVariableTaskIds.length
        && actual.every((value, index) => value === expectedVariableTaskIds[index])
      );
      const validationErrors: string[] = [];
      for (const derived of variables) {
        const variable = derived?.variable ?? {};
        const variableId = variable.id ?? 'unknown';
        if (variable.insightSource?.analysisId !== analysisId) {
          validationErrors.push(`Variable "${variableId}" belongs to a different analysis.`);
        }
        if (variable.insightSource?.taskScope !== effectiveTaskScope) {
          validationErrors.push(`Variable "${variableId}" has wrong task scope.`);
        }
        if (!sameTaskIds(variable.taskIds)) {
          validationErrors.push(`Variable "${variableId}" has wrong taskIds for the current scope.`);
        }
        for (const option of variable.options ?? []) {
          if (!availableInsightIds.has(option.id)) {
            validationErrors.push(`Variable "${variableId}" uses option "${option.id}" that is not available in this scope.`);
          }
        }
      }
      if (validationErrors.length > 0) {
        setError(`Invalid import for current scope: ${validationErrors.slice(0, 3).join(' ')}`);
        return;
      }

      // Infer categories from variables
      const categories = Array.from(new Set<string>(
        variables
          .flatMap((v: any) => v.variable?.insightSource?.categories ?? [])
          .map((category: unknown) => String(category || '').trim())
          .filter(Boolean),
      ));

      const generationId = await service.importGeneration({
        analysisId,
        analysisName: `Imported (${new Date().toLocaleDateString()})`,
        categories,
        taskScope: effectiveTaskScope,
        taskId: effectiveTaskScope === 'task' ? taskId : undefined,
        variables,
        explanation,
        source,
        collectionPolicyVersion: parsed.collectionPolicyVersion,
        questionPolicies: parsed.questionPolicies,
      });

      setSelectedGenerationId(generationId);
    } catch (err: any) {
      setError(err.message || 'Import failed — invalid JSON');
    }
  }, [service, analysisId, analysisName, aggregatedInsights, effectiveTaskScope, taskId, expectedVariableTaskIds]);

  // ── Active task progress ──────────────────────────────────────────
  const task = activeTask.task;
  const isRunning = task?.status === 'running';
  const taskPhase = (task as any)?.phase;
  const taskProgress = (task as any)?.phaseProgress;

  // Clear active task when completed
  useEffect(() => {
    if (task?.status === 'completed' || task?.status === 'error') {
      const timeout = setTimeout(() => setActiveTaskId(null), 2000);
      return () => clearTimeout(timeout);
    }
  }, [task?.status]);

  // ── Render ────────────────────────────────────────────────────────
  return (
    <Stack spacing={3}>
      {/* Generation Form */}
      <Box>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Generate Variables from Insights
        </Typography>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {effectiveTaskScope === 'task' && (!taskId || !hasTaskPackage) && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Task-scoped variables require a generated task package. Regenerate the task package first.
          </Alert>
        )}
        {effectiveTaskScope === 'global' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Generate variables for all task packages here, or select one task in the header to generate only that task.
          </Alert>
        )}
        {taskTargetsWithPackages.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No generated task packages are available for all-task variable generation.
          </Alert>
        )}

        {/* Category checkboxes */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Select categories to convert:
        </Typography>
        <FormGroup row sx={{ mb: 2 }}>
          {JTBD_CATEGORIES.map(cat => (
            <FormControlLabel
              key={cat}
              control={
                <Checkbox
                  checked={selectedCategories.includes(cat)}
                  onChange={() => toggleCategory(cat)}
                  size="small"
                  disabled={categoryCounts[cat] === 0 || isSubmitting || isRunning}
                />
              }
              label={
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="body2">{CATEGORY_LABELS[cat]}</Typography>
                  <Chip label={categoryCounts[cat]} size="small" variant="outlined" sx={{ height: 20 }} />
                </Box>
              }
            />
          ))}
        </FormGroup>

        {/* Task scope inherited from the header selector */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">Scope:</Typography>
          <Chip
            size="small"
            color={effectiveTaskScope === 'task' ? 'secondary' : 'default'}
            variant={effectiveTaskScope === 'task' ? 'filled' : 'outlined'}
            label={effectiveTaskScope === 'task' ? (taskName || 'Selected task') : 'All tasks (global)'}
          />
        </Stack>

        {/* Model selector */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">AI Model:</Typography>
          <AvailableModelSelector projectId={projectId} value={model} onChange={setModel} size="small" />
        </Stack>

        {/* Generate + Import buttons + progress */}
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button
            variant="contained"
            onClick={() => handleGenerate('selected-task')}
            disabled={selectedCategories.length === 0 || isSubmitting || isRunning || !canGenerateSelectedTask}
            startIcon={isSubmitting || isRunning ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
          >
            {isSubmitting ? 'Starting...' : isRunning ? 'Generating...' : 'Generate for selected task'}
          </Button>

          <Button
            variant={effectiveTaskScope === 'global' ? 'contained' : 'outlined'}
            onClick={() => handleGenerate('all-tasks')}
            disabled={selectedCategories.length === 0 || isSubmitting || isRunning || !canGenerateAllTasks}
            startIcon={isSubmitting || isRunning ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
          >
            Generate for all tasks
          </Button>

          <Button
            variant="outlined"
            onClick={handleExportForEval}
            startIcon={<FileDownloadIcon />}
            disabled={selectedCategories.length === 0 || isSubmitting || isRunning || !canUseSelectedTaskScope}
          >
            Export for Eval
          </Button>

          <Button
            variant="outlined"
            onClick={() => fileInputRef.current?.click()}
            startIcon={<FileUploadIcon />}
            disabled={isSubmitting || isRunning || !canUseSelectedTaskScope}
          >
            Import
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            style={{ display: 'none' }}
            onChange={handleImport}
          />

          {isRunning && taskProgress && (
            <Box sx={{ flex: 1, maxWidth: 300 }}>
              <Typography variant="caption" color="text.secondary">
                Phase {taskProgress.currentPhase}/{taskProgress.totalPhases}: {taskPhase}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={(taskProgress.currentPhase / taskProgress.totalPhases) * 100}
                sx={{ mt: 0.5 }}
              />
            </Box>
          )}
        </Stack>
      </Box>

      <Divider />

      {/* History List */}
      <Box>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Generation History
        </Typography>

        {isLoadingHistory ? (
          <CircularProgress size={20} />
        ) : scopedGenerations.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No generations for this scope yet. Select categories and generate variables.
          </Typography>
        ) : (
          <List dense disablePadding>
            {scopedGenerations.map(gen => (
              <ListItemButton
                key={gen.id}
                selected={selectedGenerationId === gen.id}
                onClick={() => setSelectedGenerationId(gen.id)}
                sx={{ borderRadius: 1, mb: 0.5 }}
              >
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">
                        {gen.categories.map(c => CATEGORY_LABELS[c] || c).join(', ')}
                      </Typography>
                      <Chip
                        label={gen.status}
                        size="small"
                        color={STATUS_COLORS[gen.status] || 'default'}
                        sx={{ height: 20 }}
                      />
                      {(gen as any).source === 'imported' || (gen as any).source === 'skill-eval' ? (
                        <Chip label="imported" size="small" variant="outlined" sx={{ height: 20 }} />
                      ) : null}
                    </Stack>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {toDate(gen.createdAt)?.toLocaleString() ?? '—'}
                      {gen.taskScope === 'task'
                        ? ` | ${taskNameById.get(gen.taskId || '') || gen.taskId || 'Per task'}`
                        : ' | Global'}
                      {gen.model ? ` | ${gen.model}` : ''}
                    </Typography>
                  }
                />
                <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {acceptedCounts[gen.id] && (
                    <Chip
                      label={`${acceptedCounts[gen.id].accepted}/${acceptedCounts[gen.id].total} accepted`}
                      size="small"
                      variant="outlined"
                      color={acceptedCounts[gen.id].accepted > 0 ? 'success' : 'default'}
                      sx={{ height: 20 }}
                    />
                  )}
                  <ChevronRightIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={(e) => { e.stopPropagation(); handleDelete(gen.id); }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItemButton>
            ))}
          </List>
        )}
      </Box>

      {/* Review Panel (expanded when generation selected; scrolled into view) */}
      {selectedGenerationId && (
        <Box ref={reviewPanelRef}>
          <Divider sx={{ mb: 2 }} />
          <InsightVariableReviewPanel
            projectId={projectId}
            generationId={selectedGenerationId}
          />
        </Box>
      )}
    </Stack>
  );
};
